import moment from 'moment';
import { ColumnApi, GridReadyEvent } from '@ag-grid-community/all-modules';
import { GridApi } from '@ag-grid-enterprise/all-modules';
import { FormState } from 'formstate';
import { patchFormValues } from 'welltrax-ui-components';
import { cloneDeep, find, get, intersectionWith, isArray, toNumber } from 'lodash';
import { action, computed, observable, reaction } from 'mobx';

import { getDocuments } from '../../../core/hooks/local_db/functions';
import { ParentPaginateStore } from '../../../core/stores/ParentPaginateStore';
import { ActionType } from '../../../core/types/ActionType';
import { IColumnResourceType, IColumnsRep } from '../../../core/types/ProfileTypes';
import { replaceInValueElement, parseDateTimeToForm } from '../utils/utils';
import vApiStatusService from '../services/VApiStatusService';

interface IParsedData {
  parseData: (data: any[], columns: IColumnsRep[]) => void;
}

export class SetupStore extends ParentPaginateStore implements IParsedData {
  @observable
  form: FormState<any>;
  @observable
  formState: ActionType;
  @observable
  drawerOpen: boolean;
  @observable
  isOpenManageFilters: boolean;
  @observable
  profileFilters: any;
  @observable
  activeProfile: string;
  @observable
  elementsForFilters: any[];
  @observable
  clearFiltersAction: boolean;
  @observable
  loadingFormData: boolean;
  @observable
  loadingVApiList: boolean;
  @observable
  vApiList: any[];
  @observable
  paramId: any;
  @observable
  originalElement: any;
  @observable
  cancelAction: boolean;

  @observable
  parsedList: any[];

  @observable
  resourcesList: any;

  @observable
  columns: IColumnsRep[];

  @observable
  isSuppressOrdering: boolean;

  @observable
  savingForm: boolean;
  @observable _gridApi: GridApi | null;
  @observable
  gridApiVAPI: GridApi | null;
  @observable
  apiStatus: boolean;
  @observable _columnApi: ColumnApi | null;
  @observable isOpenAuditTrail: boolean = false;
  @observable selectedElementsMap: Map<any, any> = new Map([]);
  @observable paginationPageSizeInfScroll: number = 100;

  constructor(serviceContainer: any, fromScenario = false) {
    super(serviceContainer);
    this.paramId = null;
    this.form = new FormState<any>({});
    this.elementsForFilters = [];
    this.activeProfile = '';
    this.profileFilters = [];
    this.loadingFormData = true;
    this.isOpenManageFilters = false;
    this.drawerOpen = false;
    this.formState = 'NONE';
    this.elements = [];
    this.element = {};
    this.vApiList = [];
    this.parsedList = [];
    this.resourcesList = {};
    this.columns = [];
    this.clearFiltersAction = false;
    this.originalElement = null;
    this.cancelAction = false;
    this.loadingVApiList = false;
    this.isSuppressOrdering = false;
    this.savingForm = false;
    this.getCommodities();
    this._gridApi = null;
    this.gridApiVAPI = null;
    this.apiStatus = false;
    this._columnApi = null;
    reaction(
      () => this.elements,
      async () => {
        if (!fromScenario) {
          if (this.elements && this.elements.length > 0) {
            if (this.paramId !== -1) {
              await this.setElementById(this.paramId || this.getId(this.elements[0]));
            } else {
              this.resetParamId();
            }
          }
        }
      }
    );
  }

  @action setIsOpenAuditTrail = (isOpenAuditTrail: boolean) => {
    this.isOpenAuditTrail = isOpenAuditTrail;
  };

  @action removeItemFormMap = (id: any) => {
    this.selectedElementsMap.delete(id);
  };

  @action setSelectedElementsMap = (selectedElementsMap: Map<any, any> = new Map([])) => {
    this.selectedElementsMap = selectedElementsMap;
  };

  handleOnGridReady = (onGridReady?: (gridApi: GridApi | null) => void) => (event: GridReadyEvent) => {
    this.setGridApi(event.api);
    this.setColumnApi(event.columnApi);
    if (this.gridApi && this.columnApi) {
      this.gridApi.sizeColumnsToFit();
      /*if (!this.profileStore.isPivotOrChartMode) {
        this.entityStore.gridApi.closeToolPanel();
      } else {
        this.entityStore.columnApi.setPivotMode(this.profileStore.form.$.mode.value === ProfileModes.PIVOT);
      }*/
      onGridReady?.(this.gridApi);
    }
  };

  markSelectedElementInTable = (ids: any[]) => {
    this.gridApi?.forEachNode?.(node => {
      node.setSelected(ids.some(id => id === node?.data?.id));
    });
  };

  @action setApiStatus = (apiStatus: boolean) => {
    this.apiStatus = apiStatus;
  };

  @action setColumnApi = (columnApi: ColumnApi | null) => {
    this._columnApi = columnApi;
  };

  @action setGridApi = (gridApi: GridApi | null) => {
    this._gridApi = gridApi;
  };

  @action setGridApiVAPI = (gridApi: GridApi | null) => {
    this.gridApiVAPI = gridApi;
  };

  @action createForm = () => {};

  @action
  setIsOpenManageFilters = (value: boolean) => {
    this.isOpenManageFilters = value;
  };

  @action setIsSavingForm = (value: boolean) => {
    this.savingForm = value;
  };

  @action setClearFiltersAction = (value: boolean) => {
    this.clearFiltersAction = value;
  };

  @action setOriginalElement = (value: any) => {
    this.originalElement = value;
  };

  @action setCancelAction = (value: boolean) => {
    this.cancelAction = value;
  };

  @action setSuppressOrdering = (value: boolean) => {
    this.isSuppressOrdering = value;
  };

  @action
  public clearFilters() {
    this.setClearFiltersAction(true);
    const commodityFilter = this.filters.find(item => item.field === 'commodityId');
    if (commodityFilter) {
      this.filters = [commodityFilter];
    } else {
      this.filters = [];
    }
    this.setPage(1);
  }

  @action updatePhoneBookList = (phoneBookList: any[]) => {
    this.setElement({ ...this.element, phoneBookList });
  };

  @action
  public clearConditionals() {
    this.conditionals = [];
  }

  @action
  setActiveProfile = (value: string) => {
    this.activeProfile = value;
  };

  @action setLoadingFormData = (value: boolean) => {
    this.loadingFormData = value;
  };

  @action
  setProfileFilters = (list: any[]) => {
    this.profileFilters = list;
  };

  @action
  setColumns(columns: IColumnsRep[]) {
    this.columns = columns;
  }

  @action
  setFormState = (state: ActionType) => {
    this.formState = state;
    if (state === 'CREATE') {
      this.createForm();
      this.setElement({});
    }
  };

  @action
  setFilterList = (list: any) => {
    this.filters = list;
    this.setPage(1);
  };

  @action getId = (element: any, key?: string) => {
    return key ? element[key].id : element.id;
  };

  @computed
  get columnApi(): ColumnApi | null {
    return this._columnApi;
  }

  @computed
  get gridApi(): GridApi | null {
    return this._gridApi;
  }

  @computed
  get loading(): boolean {
    return this.isLoading || this.loadingFormData;
  }

  @computed
  get isLoadingFormData(): boolean {
    return this.loadingFormData;
  }

  @computed
  get isReadOnlyObject(): boolean {
    return !!this.element?.isReadOnly;
  }

  @computed
  get isEditing(): boolean {
    return this.formState !== 'NONE';
  }

  @computed
  get isCreating(): boolean {
    return this.formState === 'CREATE';
  }

  @computed get isEditingOrCreating() {
    return this.isEditing || this.isCreating;
  }

  @computed
  get isFormDisabled(): boolean {
    return this.formState === 'NONE';
  }

  @computed
  get haveSelected(): boolean {
    return !!this.element.id;
  }

  public createNewElement(element: any, noGetElements = false, onSuccessful: any, onError: any) {
    this.setLoadingFormData(true);
    return this.serviceContainer
      .create(element)
      .then(async (response: any) => {
        if (response && response.data && response.data.id) {
          this.setParamId(response.data.id);
        }
        if (onSuccessful) {
          await onSuccessful(response.data);
        }
        this.showSuccessMessage();
        if (!noGetElements) {
          this.getElements(this.columns);
        } else {
          if (response.data) {
            await this.addFirstElementIntoList(response.data);
          }
        }
        this.setLoadingFormData(false);
        this.setFormState('NONE');
      })
      .catch((err: any) => {
        if (onError) {
          onError();
        }
        this.setLoadingFormData(false);
        throw err.response;
      });
  }

  public editExistingElement(element: any, noGetElements: boolean = false, onSuccessful: any, onError: any) {
    this.setLoadingFormData(true);
    this.serviceContainer
      .update(element)
      .then(async (response: any) => {
        if (response && response.data && response.data.id) {
          this.setParamId(response.data.id);
        }
        if (onSuccessful) {
          await onSuccessful(response.data);
        }
        this.showSuccessMessage();
        if (!noGetElements) {
          this.getElements(this.columns);
        } else {
          if (response && response.data) {
            await this.setElementIntoList(response.data);
          }
        }

        this.setLoadingFormData(false);
        this.setFormState('NONE');
      })
      .catch((err: any) => {
        if (onError) {
          onError();
        }
        this.setLoadingFormData(false);
        throw err.response;
      });
  }

  @action setLoadingVApiStatusList = (value: boolean) => {
    this.loadingVApiList = value;
  };

  @action setVApiStatusList = (list: any[]) => {
    this.vApiList = list;
  };

  @action setParamId = (id: number | string) => {
    this.paramId = id;
  };

  @action resetParamId = () => {
    this.paramId = null;
  };

  getVApiStatusList = async (type: string) => {
    try {
      this.setLoadingVApiStatusList(true);
      const response = await vApiStatusService.getAll(type);
      this.setVApiStatusList(response?.data || []);
      this.setLoadingVApiStatusList(false);
    } catch (err) {
      this.setLoadingVApiStatusList(false);
    }
  };

  @action cleanStore = () => {
    this.search = '';
    this.destroyGrid();
    this.elementsForFilters = [];
    this.activeProfile = '';
    this.profileFilters = [];
    this.loadingFormData = false;
    this.isOpenManageFilters = false;
    this.filters = [];
    this.orders = [];
    this.drawerOpen = false;
    this.formState = 'NONE';
    this.isLoading = false;
    this.elements = [];
    this.parsedList = [];
    this.element = {};
    this.count = 0;
    this.page = 1;
    this.size = 50;

    this.gridApiVAPI = null;
    this.form?.reset?.();
    this.setFetchedDataFromServer(false);
  };

  @action destroyGrid = () => {
    this._gridApi = null;
    this._columnApi = null;
  };

  @action
  public setElement(element: any) {
    if (element && element.contact) {
      this.element = { ...element, ...element.contact };
    } else {
      this.element = element;
    }
  }

  @action setElementsForFilters = (elements: any) => {
    this.elementsForFilters = elements;
  };

  @action
  setDrawerOpen = (value: boolean) => {
    this.drawerOpen = value;
  };

  getCommodities = async () => {
    getDocuments('util/commodities', 'Commodity', (commodities: any) => {
      this.setResourcesList({ ...this.resourcesList, ...{ commodities } });
    });
  };

  getDatesObject = (date: Date): any => {
    if (!date) {
      return {
        time: '00:00',
        date: ''
      };
    }
    const timeInMilliseconds = moment(date).toDate()?.getTime();

    return {
      timeInMilliseconds,
      empty: false,
      date: moment(date).format('MM/DD/YYYY'),
      javaDate: `${moment(date).format('MMM DD, YYYY')}  12:00:00 AM`,
      sqldate: moment(date).format('YYYY-MM-DD'),
      sqldateWithQuotes: `'${moment(date).format('YYYY-MM-DD')}'`,
      sqltimestamp: `${moment(date).format('MMM DD, YYYY')}  12:00:00 AM`,
      sqltimestampWithMilliseconds: `${moment(date).format('MMM DD, YYYY')}  12:00:00 AM`,
      sqltimestampWithQuotes: `'${moment(date).format('YYYY-MM-DD 00:00:00.0')}'`,
      time: '00:00',
      timeInMinutes: timeInMilliseconds / 60000
    };
  };

  @action setElementById = async (id: string | number): Promise<void> => {
    await this.getElementById(id);
  };

  @action getElementById = async (id: string | number): Promise<void> => {
    if (id) {
      this.setLoadingFormData(true);
      this.setElement({});
      try {
        const response = await this.serviceContainer.getById(id);
        this.setElement(response.data);
        this.resetSelectedElements();
        this.setLoadingFormData(false);
      } catch (e) {
        this.setLoadingFormData(false);
        throw (e as Error).message;
      }
    }
  };

  public async setElementsResponse(data: any[], columns?: IColumnsRep[]) {
    if (data) {
      this.setElements(data);
      if (columns) {
        this.setParsedList([]);
        this.setColumns(columns);
        let result = await this.parseData(data, columns);
        result = this.orderStaticData(result);
        this.setParsedList(result);
      }
    } else {
      this.setParsedList([]);
      this.setElements([]);
    }
  }

  buildPaginationParams(pageHeader: any) {
    if (pageHeader.hasOwnProperty('pageable')) {
      const pageable = JSON.parse(pageHeader.pageable);
      if (
        pageable.hasOwnProperty('offset') &&
        pageable.hasOwnProperty('total_data') &&
        pageable.hasOwnProperty('current_page')
      ) {
        this.setCount(toNumber(pageable.total_data));
        this.setPage(toNumber(pageable.current_page));
        this.setSize(toNumber(pageable.offset));
      }
    } else {
      this.setCount(this.elements.length);
    }
  }

  selectDefaultElement() {
    if (this.elements.length > 0) {
      let id = this.paramId;
      if (!id) {
        if (this.parsedList.length > 0 && this.parsedList[0].hasOwnProperty('id')) {
          id = this.parsedList[0].id;
        } else {
          if (this.elements[0].hasOwnProperty('id')) {
            id = this.elements[0].id;
          }
        }

        this.getElementById(id).then((res: any) => {
          if (res) {
            patchFormValues(this.form, this.element);
          }
        });
      }
    }
  }

  public getElements(columns?: IColumnsRep[]): Promise<boolean> {
    this.setLoading(true);
    this.setFormState('NONE');
    this.setElement({});
    this.resetParamId();
    this.setSuppressOrdering(false);
    this.setColumns(columns ?? []);
    if (this.page === 0 || this.size === 0) {
      this.setPage(1);
      this.setSize(50);
    }
    const filters = this.getTransformFiltersToBackend();
    return this.serviceContainer
      .getPaginateAll(this.page, this.size, this.orders, filters, this.search)
      .then(async (response: any) => {
        const data = response.data;
        if (!data || data.length === 0) {
          this.setCount(0);
          this.setPage(1);
          this.setSize(0);
          this.createForm();
          this.setElements([]);
          this.setParsedList([]);
          this.setLoadingFormData(false);
        } else {
          await this.setElementsResponse(data, columns);
          this.buildPaginationParams(response.headers);
          this.resetSelectedElements();
          this.selectDefaultElement();
        }

        this.setLoading(false);
        return Promise.resolve(true);
      })
      .catch(() => {
        this.setLoading(false);
        return Promise.resolve(false);
      });
  }

  updateSelectedElementAndList = (element: any) => {
    const newElements = cloneDeep(this.elements);
    const isInList = newElements.some((item, index) => {
      const result = item?.id === element?.id;
      if (result) {
        newElements[index] = element;
      }
      return result;
    });
    if (isInList) {
      this.setParamId(element?.id);
      this.setElements(newElements);
    }
    return isInList;
  };

  @action setParsedList(item: any[]) {
    this.parsedList = item;
  }

  @action setResourcesList(item: any) {
    this.resourcesList = item;
  }

  @computed
  get disabled(): boolean {
    return this.formState === 'NONE';
  }

  /***
   *   Logic for the parsing of the columns according to the profile
   */
  parseData(data: any[], columns: IColumnsRep[]): Promise<any[]> {
    try {
      const plists: any[] = [];
      for (const d of data) {
        const parseObj: any = this.parseElementToData(d, columns);
        plists.push(parseObj);
      }
      return Promise.resolve(plists);
    } catch (e) {
      return Promise.resolve([]);
    }
  }

  parseElementToData(element: any, columns: IColumnsRep[]) {
    const parseObj: any = {};
    for (const column of columns) {
      try {
        if (column.source) {
          switch (column.sourceType) {
            case 'OBJECT':
              if (element.hasOwnProperty(column.source)) {
                parseObj[column.name] = replaceInValueElement(
                  element[column.source],
                  column.type,
                  column.replaceInValue
                );
              }
              break;
            case 'CONCAT_OBJECT':
              parseObj[column.name] = replaceInValueElement(
                this.getConcatObject(element, column.source, column.defaultValue),
                column.type,
                column.replaceInValue
              );
              break;
            case 'ARRAY_CONCAT':
              parseObj[column.name] = this.getArrayConcat(element, column.source, column.concatSym || ',');
              break;
            case 'STRING_CONCAT_OBJECT':
              parseObj[column.name] = this.getStringConcatObject(element, column.source, column.concatSym || ',');
              break;
            case 'URL_ARRAY_CONCAT_OBJECT':
              parseObj[column.name] = this.getURLArrayConcatObject(
                element,
                column.source,
                column.url || '',
                column.concatSym || ','
              );
              break;
            case 'ARRAY_ZERO_CONCAT_OBJECT':
              parseObj[column.name] = this.getArrayZeroConcatObject(element, column.source);
              break;
            case 'ARRAY_ZERO_ARRAY_CONCAT_OBJECT':
              parseObj[column.name] = this.getArrayZeroArrayConcatObject(
                element,
                column.source,
                column.concatSym || ','
              );
              break;
            case 'ARRAY_CONCAT_OBJECT':
              parseObj[column.name] = this.getArrayConcatObject(element, column.source, column.concatSym || ',');
              break;
            case 'ARRAY_CONCAT_OBJECT_ACTIVE':
              parseObj[column.name] = this.getArrayConcatObjectActive(
                element,
                column.source,
                column.sourceValidation || '',
                column.concatSym || ','
              );
              break;
            case 'ARRAY_ZERO_STRING_CONCAT_OBJECT':
              parseObj[column.name] = this.getArrayZeroStringConcatObject(
                element,
                column.source,
                column.concatSym || ','
              );
              break;
            case 'ARRAY_ZERO_URL_ARRAY_CONCAT_OBJECT':
              parseObj[column.name] = this.getArrayZeroUrlArrayConcatObject(
                element,
                column.source,
                column.url || '',
                column.concatSym || ','
              );
              break;
            case 'ARRAY_ZERO_ARRAY_CONCAT':
              parseObj[column.name] = this.getArrayZeroArrayConcat(element, column.source, column.concatSym || ',');
              break;
            case 'RESOURCE_OBJECT':
              parseObj[column.name] = this.getResourceObject(element, column.source, column.resource || null);
              break;
            case 'ARRAY_ZERO_RESOURCE_OBJECT':
              parseObj[column.name] = this.getArrayZeroResourceObject(element, column.source, column.resource || null);
              break;
            case 'CONCAT_OBJECT_ARRAY_ZERO_ARRAY_CONCAT_OBJECT':
              parseObj[column.name] = this.getConcatObjectArrayZeroArrayConcatObject(element, column.source);
              break;
            case 'CONDITIONAL_CONCAT_OBJECT':
              parseObj[column.name] = this.getConditionalConcat(element, column.source);
              break;
            case 'ARRAY_CONCAT_OBJECT_CONTACT_STRING':
              parseObj[column.name] = this.getArrayConcatObjectConcactString(
                element,
                column.source,
                column.concatSym,
                column.concatMainSym
              );
              break;
            case 'CONCAT_DATE_TIME_OBJECT':
              parseObj[column.name] = parseDateTimeToForm(
                this.getConcatObject(element, column.source, column.defaultValue),
                'DATE_TIME'
              );
          }
        }
      } catch (e) {
        parseObj[column.name] = '';
      }
    }
    return parseObj;
  }

  /**
   * For source "CONCAT_OBJECT" like contact/country
   * */
  getConcatObject(item: any, source: string, defaultValue?: any) {
    const obj = source.split('/');
    let concat = item;
    for (const o of obj) {
      if (concat && concat.hasOwnProperty(o)) {
        concat = concat[o];
      } else {
        concat = '';
        break;
      }
    }
    if (concat === '' && defaultValue) {
      concat = defaultValue;
    }
    return concat;
  }

  /**
   * For sourceType "STRING_CONCAT_OBJECT" like contact/streetLine1,contact/streetLine2,contact/streetLine3
   * */
  getStringConcatObject(item: any, source: string, separator: string) {
    const list = source.split(',');
    const concat = [];
    for (const l of list) {
      const co = this.getConcatObject(item, l);
      if (!!co?.length) {
        concat.push(co);
      }
    }
    return !!concat.length
      ? concat.filter(value => value).join(separator === ',' ? `${separator} ` : ` ${separator} `)
      : '';
  }

  /**
   * For sourceType "URL_ARRAY_CONCAT_OBJECT" like commodityList/name
   * */
  getURLArrayConcatObject(item: any, source: string, url: string, separator: string) {
    const obj = source.split('/');
    let list = [];
    if (obj.length > 1 && item.hasOwnProperty(obj[0])) {
      if (url.search('commodities') !== -1) {
        if (this.resourcesList.hasOwnProperty('commodities')) {
          const comm = this.resourcesList['commodities'];
          list = this.matchInList(item[obj[0]], comm);
        }
      }
    }
    if (obj[1]) {
      return this.getConcatArrayObject(list, obj[1], separator);
    }
    return '';
  }

  /**
   * Search id in Array an return objects match with this ids.
   * */
  matchInList(sourceList: any[], list: any[]) {
    return intersectionWith(list, sourceList, (t1: any, t2: any) => {
      return t1.id === t2;
    });
  }

  /**
   * Concat Items in array using object key
   * */
  getConcatArrayObject(item: any[], source: string, separator: string) {
    if (item && item.length > 0 && source) {
      return item.map(value => (value.hasOwnProperty(source) ? value[source] : '')).join(`${separator} `);
    }
    return '';
  }

  /**
   * For sourceType "ARRAY_CONCAT" like commodityList
   * */
  getArrayConcat(item: any, source: string, separator: string) {
    if (item && item.hasOwnProperty(source) && item[source].length > 0) {
      return item[source].join(`${separator} `);
    }
    return '';
  }

  /**
   * For sourceType "ARRAY_ZERO_CONCAT_OBJECT" like pickUpList-leaseNumberAlphaNumeric
   * */
  getArrayZeroConcatObject(item: any, source: string) {
    const split = source.split('-');
    if (split.length > 1 && item.hasOwnProperty(split[0]) && item[split[0]] && item[split[0]].length > 0) {
      return this.getConcatObject(item[split[0]][0], split[1]);
    }
    return '';
  }

  /**
   * For sourceType "ARRAY_ZERO_ARRAY_CONCAT_OBJECT" like pickUpList-terminalList/name
   * */
  getArrayZeroArrayConcatObject(item: any, source: string, separator: string) {
    const split = source.split('-');
    if (split.length > 1 && item.hasOwnProperty(split[0]) && item[split[0]] && item[split[0]].length > 0) {
      const auxSource = split[1].split('/');
      const list = item[split[0]][0];
      const resSource = auxSource.splice(1, auxSource.length).join('/');
      return this.getArrayConcatObject(list, `${auxSource}-${resSource}`, separator);
    }
    return '';
  }

  /**
   * For sourceType "ARRAY_CONCAT_OBJECT" like accountDistrictList-contact/fullName
   * */
  getArrayConcatObject(item: any, source: string, separator: string) {
    const split = source.split('-');
    const concat = [];
    if (split.length > 1 && item.hasOwnProperty(split[0])) {
      const key = split[0];
      if (item[key] && item[key].length > 0) {
        for (const l of item[key]) {
          if (l) {
            concat.push(this.getConcatObject(l, split[1]));
          }
        }
      }
    }

    return `${concat.filter(value => value).join(`${separator} `)}`;
  }
  /**
   * For sourceType "ARRAY_CONCAT_OBJECT_ACTIVE" like accountDistrictList-contact/fullName but just the active ones
   * based on sourceValidation key in profile like accountDistrictList-isActive
   * */
  getArrayConcatObjectActive(item: any, source: string, sourceValidation: string, separator: string) {
    const split = source.split('-');
    const splitVal = sourceValidation.split('-');
    const concat = [];
    if (split.length > 1 && item.hasOwnProperty(split[0])) {
      const key = split[0];
      if (item[key] && item[key].length > 0) {
        for (const l of item[key]) {
          if (l && get(l, splitVal?.[1], false)) {
            concat.push(this.getConcatObject(l, split[1]));
          }
        }
      }
    }

    return `${concat.filter(value => value).join(`${separator} `)}`;
  }

  /**
   * For sourceType "ARRAY_CONCAT_OBJECT_CONTACT_STRING" concat the result of ARRAY_CONCAT_OBJECT
   * */
  getArrayConcatObjectConcactString(item: any, source: string, separator = ',', MainSeparator = '/') {
    const split = source.split(',');
    const left = this.getArrayConcatObject(item, split[0], separator);
    const rigth = this.getArrayConcatObject(item, split[1], separator);
    if (!!left && !!rigth) return `${left} ${MainSeparator} ${rigth}`;
    if (!!left && !rigth) return `${left}`;
    if (!left && !!rigth) return `${rigth}`;
    return '';
  }

  /**
   * For sourceType "ARRAY_ZERO_STRING_CONCAT_OBJECT" like terminalList-contact/streetLine1,contact/streetLine2,contact/streetLine3
   * */
  getArrayZeroStringConcatObject(item: any, source: string, separator: string) {
    const split = source.split('-');
    if (split.length > 1 && item.hasOwnProperty(split[0]) && item[split[0]] && item[split[0]].length > 0) {
      return this.getStringConcatObject(item[split[0]][0], split[1], separator);
    }
    return '';
  }

  /**
   * For sourceType "ARRAY_ZERO_URL_ARRAY_CONCAT_OBJECT" like terminalList-commodityList/name
   * */
  getArrayZeroUrlArrayConcatObject(item: any, source: string, url: string, separator: string) {
    const split = source.split('-');
    if (split.length > 1 && item.hasOwnProperty(split[0]) && item[split[0]] && item[split[0]].length > 0) {
      return this.getURLArrayConcatObject(item[split[0]][0], split[1], url, separator);
    }
    return '';
  }

  /**
   * For sourceType "ARRAY_ZERO_ARRAY_CONCAT" like terminalList-commodityList
   * */
  getArrayZeroArrayConcat(item: any, source: string, separator: string) {
    const split = source.split('-');
    if (split.length > 1 && item.hasOwnProperty(split[0]) && item[split[0]] && item[split[0]].length > 0) {
      return this.getArrayConcat(item[split[0]][0], split[1], separator);
    }
    return '';
  }

  /**
   * For sourceType "RESOURCE_OBJECT" useful for get items element in resourceList
   * */
  getResourceObject(item: any, source: string, resource: IColumnResourceType | null) {
    if (source && resource && resource.resource) {
      if (this.resourcesList.hasOwnProperty(resource.resource) && item.hasOwnProperty(source)) {
        const element = find(this.resourcesList[resource.resource], value => value.id === item[source]);
        if (element && element.hasOwnProperty(resource.property)) {
          return element[resource.property];
        }
      }
    }
    return '';
  }

  /**
   * For sourceType "ARRAY_ZERO_RESOURCE_OBJECT" useful for get items element in resourceList like pickupList-commodityId
   * */
  getArrayZeroResourceObject(item: any, source: string, resource: IColumnResourceType | null) {
    const split = source.split('-');
    if (split.length > 1 && item.hasOwnProperty(split[0]) && item[split[0]] && item[split[0]].length > 0) {
      return this.getResourceObject(item[split[0]][0], split[1], resource);
    }

    return '';
  }

  /**
   * For sourceType "CONCAT_OBJECT_ARRAY_ZERO_ARRAY_CONCAT_OBJECT" like pickUpAccount/accountDistrictList-contact/fullName
   * */
  getConcatObjectArrayZeroArrayConcatObject(item: any, source: string) {
    const split = source.split('-');
    if (split.length > 1) {
      const object = this.getConcatObject(item, split[0], []);
      if (object && isArray(object) && object.length > 0) {
        return this.getConcatObject(object[0], split[1]);
      }
    }
    return '';
  }

  /**
   * For sourceType "CONDITIONAL_CONCAT_OBJECT" when the value depend of other value to select the source
   * */
  getConditionalConcat(item: any, source: string) {
    const split = source.split('?');
    if (split.length > 1) {
      const object = this.getConcatObject(item, split[0], '');
      if (object) {
        return object;
      }
      return this.getConcatObject(item, split[1], '');
    }
    return '';
  }

  clearDataWhenChangeCommodity(dataResources: any) {}

  setElementIntoList = (element: any): Promise<any> => {
    return new Promise(async resolve => {
      const data = await this.findAndReplaceElementInList(element);
      this.setElementsResponse(data, this.columns);
      resolve(true);
    });
  };

  findAndReplaceElementInList = (element: any, key?: string): Promise<any[]> => {
    const results = cloneDeep(this.elements);
    const indexElement = this.elements.findIndex((el: any) => {
      if (key) {
        const elKey = get(el, key);
        const elementKey = get(element, key);
        return elKey && elementKey && elKey === elementKey;
      }
      return el.id === element.id;
    });
    if (indexElement !== -1) {
      results[indexElement] = element;
    } else {
      results.unshift(element);
    }

    return Promise.resolve(results);
  };

  addFirstElementIntoList = async (element: any, notSetElement?: boolean) => {
    this.setSuppressOrdering(true);
    const items: any[] = cloneDeep(this.parsedList);
    const result = await this.parseData([element], this.columns);
    const newParseList = [...result, ...items];
    this.setParsedList(newParseList);

    const excludeAction = ['EDIT', 'NONE'];

    if (!excludeAction.includes(this.formState)) {
      this.setCount(this.count + 1);
    }
    this.setFormState('NONE');
    !notSetElement && (await this.setElementById(newParseList[0].id));
  };

  @computed get formDisabled() {
    return this.formState === 'NONE' || this.isLoadingForm;
  }

  @computed get disableOnEditOrCreate() {
    return this.formState !== 'NONE' || this.isLoadingForm;
  }

  @action
  public setLoading(value: boolean) {
    this.isLoading = value;
    if (this.isLoading) {
      this.gridApi?.destroy();
      this.setGridApi(null);
      this.setColumnApi(null);
    }
  }

  @computed get isLoadingForm(): boolean {
    return this.isLoading || this.loadingFormData;
  }

  @computed get loadingData() {
    return this.isLoading;
  }

  @computed get loadingVAPIData() {
    return this.loadingVApiList;
  }

  @computed get loadingAnyTable() {
    return this.loadingData || this.loadingVAPIData;
  }

  @computed get quantity() {
    return !this.apiStatus ? this.count || 0 : this.vApiList.length || 0;
  }

  @computed get elementId() {
    return this.element?.id;
  }

  @computed get alreadyFetchedData() {
    return this.fetchedDataFromServer;
  }

  getDataOrderedInTable = () => {
    const dataOrdered: any = [];
    this.gridApi?.forEachNodeAfterFilterAndSort?.(node => {
      dataOrdered.push(node?.data);
    });
    return dataOrdered;
  };

  handleNewButton = () => {
    if (this.element) {
      this.setOriginalElement(cloneDeep(this.element));
    }
    this.setFormState('CREATE');
  };

  handleEditButton = () => {
    if (this.element) {
      this.setOriginalElement(cloneDeep(this.element));
    }
    this.setFormState('EDIT');
  };

  @observable isOpenCancelEditionDialog: boolean = false;
  @action setIsOpenCancelEditionDialog = (value: boolean) => {
    this.isOpenCancelEditionDialog = value;
  };

  toggleIsOpen = () => {
    this.formState === 'NONE'
      ? this.setDrawerOpen(false)
      : this.setIsOpenCancelEditionDialog(!this.isOpenCancelEditionDialog);
  };

  handleCancelConfirmation =
    (docCallBack?: () => void, noResetStoreElementOnCancel?: boolean, handleCancel?: () => void) => () => {
      this.toggleIsOpen();
      if (this.formState === 'CREATE') {
        this.setDrawerOpen(false);
        if (!noResetStoreElementOnCancel) {
          this.setElement({});
          this.createForm();
        }
      } else {
        this.setCancelAction(true);
      }
      if (this.formState === 'EDIT' || this.formState === 'NONE') {
        this.setDrawerOpen(false);
        this.createForm();
      } else {
        this.setCancelAction(true);
      }
      if (this.originalElement && !noResetStoreElementOnCancel) {
        this.setElement(this.originalElement);
      }
      this.setFormState('NONE');
      handleCancel?.();
      docCallBack?.();
    };
}
